import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, TrendingUp, FileText, Rocket, AlertCircle, BarChart3, Package, ShoppingBag, Wrench, List } from 'lucide-react';
import theme from '../theme';
import FirstTimeChecklist from '../components/FirstTimeChecklist';
import PlanUsageCard from '../components/PlanUsageCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatorDashboardWorking = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorProfile, setCreatorProfile] = useState(null);
  // P2.3: Dashboard summary state
  const [summary, setSummary] = useState({
    active_portfolios: null,
    contracts_remaining: null,
    ready_to_launch: null,
    pending_actions: null
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // P2.3 + P5.0: Fetch summary, portfolios, and creator profile in parallel
      const [summaryRes, portfoliosRes, profileRes] = await Promise.all([
        axios.get(`${API}/creator/dashboard/summary`, { headers }),
        axios.get(`${API}/creator/portfolios`, { headers }),
        axios.get(`${API}/creator/profile`, { headers }).catch(() => null)
      ]);
      
      setSummary(summaryRes.data.summary);
      setPortfolios(portfoliosRes.data.portfolios || []);
      if (profileRes?.data?.profile) setCreatorProfile(profileRes.data.profile);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasMode = (mode) => {
    if (!creatorProfile?.creator_modes) return true;
    return creatorProfile.creator_modes.includes(mode) || creatorProfile.creator_modes.includes('BOTH');
  };

  const DashboardTile = ({ icon: Icon, title, value, color, route, testIdOverride }) => (
    <div
      onClick={() => navigate(route)}
      data-testid={testIdOverride || `tile-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: theme.colors.bg.card,
        border: `1px solid ${theme.colors.borders}`,
        borderRadius: theme.radius.card,
        padding: '24px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: theme.colors.shadows.sm
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = theme.colors.shadows.lg;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme.colors.shadows.sm;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={24} style={{ color }} />
        </div>
      </div>
      <div style={{
        fontSize: '32px',
        fontWeight: 700,
        color: theme.colors.text.primary,
        marginBottom: '8px'
      }}>
        {value !== null ? value : '—'}
      </div>
      <div style={{
        fontSize: '14px',
        fontWeight: 500,
        color: theme.colors.text.secondary
      }}>
        {title}
      </div>
    </div>
  );

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading dashboard...</div>;
  }

  return (
    <div style={{ padding: '40px' }}>
      <PlanUsageCard />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
            Creator Dashboard
          </h1>
          <p style={{ fontSize: '16px', color: theme.colors.text.secondary }}>
            Welcome back, {user?.name || 'Creator'}!
          </p>
        </div>
        <button
          data-testid="creator-btn-create-portfolio"
          onClick={() => navigate('/creator/portfolios/new')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: theme.colors.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <Plus size={20} />
          Create Portfolio
        </button>
      </div>

      <FirstTimeChecklist role="creator" userId={user?.id} />

      {/* Dashboard Tiles - P2.3: Now using real data from summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <DashboardTile
          icon={TrendingUp}
          title="Active Kickbacks"
          value={summary.active_portfolios}
          color={theme.colors.neon.green}
          route="/creator/portfolios?filter=active"
          testIdOverride="creator-summary-active"
        />
        <DashboardTile
          icon={FileText}
          title="Contracts Remaining"
          value={summary.contracts_remaining}
          color={theme.colors.neon.blue}
          route="/creator/contracts"
          testIdOverride="creator-summary-contracts"
        />
        <DashboardTile
          icon={Rocket}
          title="Ready to Launch"
          value={summary.ready_to_launch}
          color={theme.colors.neon.purple}
          route="/creator/ready-to-launch"
        />
        <DashboardTile
          icon={AlertCircle}
          title="Pending Actions"
          value={summary.pending_actions}
          color={theme.colors.neon.pink}
          route="/creator/pending-actions"
        />
      </div>

      {/* P5.0: Utilities Section */}
      <div style={{
        background: theme.colors.bg.card,
        border: `1px solid ${theme.colors.borders}`,
        borderRadius: theme.radius.card,
        padding: '24px',
        marginBottom: '32px',
        boxShadow: theme.colors.shadows.sm
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '16px' }}>
          Utilities
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div
            data-testid="tile-utilities-marketplace"
            onClick={() => navigate('/creator/utilities-marketplace')}
            style={{
              padding: '18px 16px',
              background: theme.colors.bg.secondary,
              borderRadius: theme.radius.sm,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: `1px solid ${theme.colors.borders}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = theme.colors.bg.tertiary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = theme.colors.bg.secondary; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${theme.colors.neon.blue}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBag size={20} style={{ color: theme.colors.neon.blue }} />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Marketplace</div>
              <div style={{ fontSize: '12px', color: theme.colors.text.muted }}>Browse utilities</div>
            </div>
          </div>

          {hasMode('UTILITY_PROVIDER') && (
            <div
              data-testid="tile-publish-utility"
              onClick={() => navigate('/creator/publish-utility')}
              style={{
                padding: '18px 16px',
                background: theme.colors.bg.secondary,
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: `1px solid ${theme.colors.borders}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = theme.colors.bg.tertiary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = theme.colors.bg.secondary; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${theme.colors.accent.teal}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Wrench size={20} style={{ color: theme.colors.accent.teal }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Publish Utility</div>
                <div style={{ fontSize: '12px', color: theme.colors.text.muted }}>Create listing</div>
              </div>
            </div>
          )}

          {hasMode('UTILITY_PROVIDER') && (
            <div
              data-testid="tile-my-listings"
              onClick={() => navigate('/creator/my-listings')}
              style={{
                padding: '18px 16px',
                background: theme.colors.bg.secondary,
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: `1px solid ${theme.colors.borders}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = theme.colors.bg.tertiary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = theme.colors.bg.secondary; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${theme.colors.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <List size={20} style={{ color: theme.colors.primary }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>My Listings</div>
                <div style={{ fontSize: '12px', color: theme.colors.text.muted }}>Manage offerings</div>
              </div>
            </div>
          )}

          {hasMode('PORTFOLIO_CREATOR') && (
            <div
              data-testid="tile-create-portfolio"
              onClick={() => navigate('/creator/portfolios/new')}
              style={{
                padding: '18px 16px',
                background: theme.colors.bg.secondary,
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: `1px solid ${theme.colors.borders}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = theme.colors.bg.tertiary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = theme.colors.bg.secondary; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${theme.colors.neon.green}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Plus size={20} style={{ color: theme.colors.neon.green }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Create Portfolio</div>
                <div style={{ fontSize: '12px', color: theme.colors.text.muted }}>Launch new NFTs</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Your Portfolios */}
      <div style={{
        background: theme.colors.bg.card,
        border: `1px solid ${theme.colors.borders}`,
        borderRadius: theme.radius.card,
        padding: '24px',
        boxShadow: theme.colors.shadows.sm
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text.primary }}>
            Your Portfolios
          </h3>
          <button
            onClick={() => navigate('/creator/portfolios/new')}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              color: theme.colors.primary,
              border: `1px solid ${theme.colors.primary}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            + Create Portfolio
          </button>
        </div>
        {portfolios.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {portfolios.map((portfolio) => (
              <div
                key={portfolio.id}
                onClick={() => navigate(`/creator/portfolios/${portfolio.id}`)}
                style={{
                  padding: '16px',
                  background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: `1px solid ${theme.colors.borders}`
                }}
                onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.tertiary}
                onMouseOut={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
              >
                <div style={{
                  width: '100%',
                  height: '120px',
                  background: portfolio.nft_image_url || `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.neon.blue})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: theme.radius.sm,
                  marginBottom: '12px'
                }} />
                <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  {portfolio.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: portfolio.status === 'ACTIVE' ? theme.colors.neon.green : theme.colors.text.muted,
                  textTransform: 'uppercase'
                }}>
                  {portfolio.status}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: '16px', color: theme.colors.text.muted, marginBottom: '16px' }}>
              No portfolios yet
            </div>
            <button
              onClick={() => navigate('/creator/portfolios/new')}
              style={{
                padding: '10px 20px',
                background: theme.colors.primary,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: theme.radius.sm,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Create Your First Portfolio
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorDashboardWorking;
